import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["preview", "mainForm", "additionalInfo"];
  static values = {
    storageKey: String,
  };

  connect() {
    // Initialize data list, checking sessionStorage for preserved data
    this.dataList = this.retrieveDataFromStorage() || [];

    // Restore previews if we have saved data
    if (this.dataList.length > 0) {
      this.restorePreviewsFromData();
    }
  }

  // Save data to sessionStorage before page unloads
  disconnect() {
    this.saveDataToStorage();
  }

  // Handle file selection from input
  fileSelected(event) {
    const input = event.target;
    const files = input.files;

    // Check photo limit
    if (this.wouldExceedPhotoLimit(files.length)) {
      this.showErrorMessage(
        `You can upload a maximum of ${this.maxPhotos} photos`,
      );
      input.value = "";
      return;
    }

    this.clearErrorMessages();

    // Process selected files
    Array.from(files).forEach((file, index) => {
      const id = `${Date.now()}_${index}`;
      const reader = new FileReader();

      reader.onload = (e) => {
        // Store both file and dataURL for persistence
        this.dataList.push({
          id,
          file,
          dataURL: e.target.result,
          name: file.name,
          type: file.type,
        });
        this.displayPreviewFromData(this.dataList[this.dataList.length - 1]);
        this.updatePhotoCountDisplay();
        // Save to storage after each file is processed
        this.saveDataToStorage();
      };

      reader.readAsDataURL(file);
    });

    // Reset input after a delay
    setTimeout(() => {
      input.value = ""; // Reset input to allow selecting the same file again
    }, 1500);
  }

  // Display preview using data object
  displayPreviewFromData(data) {
    const container = this.createPreviewContainer(data.id);
    const img = this.createPreviewImage(data.dataURL, data.name);
    const deleteButton = this.createDeleteButton(data.id);

    container.appendChild(img);
    container.appendChild(deleteButton);

    // Add to DOM
    this.previewTarget.prepend(container);

    // Hide the default preview if needed
    this.updateDefaultPreviewVisibility();
  }

  // Restore all previews from data
  restorePreviewsFromData() {
    this.dataList.forEach((data) => {
      this.displayPreviewFromData(data);
    });
    this.updatePhotoCountDisplay();
    this.updateDefaultPreviewVisibility();
  }

  // Old display preview method, no longer used directly
  displayPreview(file, id) {
    const reader = new FileReader();

    reader.onload = (e) => {
      const container = this.createPreviewContainer(id);
      const img = this.createPreviewImage(e.target.result, file.name);
      const deleteButton = this.createDeleteButton(id);

      container.appendChild(img);
      container.appendChild(deleteButton);

      // Add to DOM
      this.previewTarget.prepend(container);

      // Hide the default preview if needed
      this.updateDefaultPreviewVisibility();
    };

    reader.readAsDataURL(file);
  }

  // Save current data to sessionStorage
  saveDataToStorage() {
    try {
      // Create a serializable version of our data
      const storableData = this.dataList.map((item) => ({
        id: item.id,
        dataURL: item.dataURL,
        name: item.name,
        type: item.type,
      }));

      // Store in sessionStorage
      const key = this.hasStorageKeyValue
        ? this.storageKeyValue
        : "imagePreviewData";
      sessionStorage.setItem(key, JSON.stringify(storableData));
    } catch (error) {
      console.error("Error saving image data to storage:", error);
    }
  }

  // Retrieve data from sessionStorage
  retrieveDataFromStorage() {
    try {
      const key = this.hasStorageKeyValue
        ? this.storageKeyValue
        : "imagePreviewData";
      const storedData = sessionStorage.getItem(key);
      if (!storedData) return null;

      const parsedData = JSON.parse(storedData);

      // Return the parsed data
      return parsedData.map((item) => {
        // Recreate File objects from the dataURLs if possible
        return {
          id: item.id,
          dataURL: item.dataURL,
          name: item.name,
          type: item.type,
          // Create a new file from the dataURL
          file: this.dataURLToFile(item.dataURL, item.name, item.type),
        };
      });
    } catch (error) {
      console.error("Error retrieving image data from storage:", error);
      return null;
    }
  }

  // Convert dataURL back to File
  dataURLToFile(dataURL, filename, type) {
    try {
      // Convert base64 to blob
      const arr = dataURL.split(",");
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      // Create file from blob
      return new File([u8arr], filename, { type: type || mime });
    } catch (error) {
      console.error("Error converting dataURL to File:", error);
      return null;
    }
  }

  // Remove a file from the preview and data
  deleteFile(event) {
    const fileId = event.currentTarget.dataset.fileId;

    // Remove from dataList
    this.dataList = this.dataList.filter((item) => item.id !== fileId);

    // Save updated data to storage
    this.saveDataToStorage();

    // Remove from DOM
    const container = this.previewTarget.querySelector(
      `[data-file-id="${fileId}"]`,
    );
    if (container) {
      container.remove();
      this.updateDefaultPreviewVisibility();
      this.updatePhotoCountDisplay();
    }
  }

  // Clear all stored data (useful when form is submitted)
  clearStoredData() {
    const key = this.hasStorageKeyValue
      ? this.storageKeyValue
      : "imagePreviewData";
    sessionStorage.removeItem(key);
    this.dataList = [];
  }

  // Handle form submission with files
  handleSubmit(event) {
    event.preventDefault();

    const form = this.mainFormTarget;
    const formData = this.getFormData(form);

    fetch(form.action, {
      method: form.method || "POST",
      body: formData,
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        Accept: "text/html,application/xhtml+xml,application/xml",
      },
      redirect: "follow",
    })
      .then((response) => {
        if (response.redirected) {
          // Clear storage before redirecting
          this.clearStoredData();
          window.Turbo.visit(response.url, { action: "replace" });
        } else if (response.ok) {
          // Clear storage before reloading
          this.clearStoredData();
          window.Turbo.visit(window.location.href, { action: "replace" });
        } else {
          console.error("Error submitting form:", response.status);
        }
      })
      .catch((error) => {
        console.error("Network error during form submission:", error);
      });
  }

  // HELPER METHODS

  get maxPhotos() {
    return parseInt(this.element.dataset.maxPhotos || 2, 10);
  }

  get currentPhotoCount() {
    const attachedCount = this.previewTarget.querySelectorAll(
      ".diary-inputform__foto:not([data-file-id])",
    ).length;
    const previewCount = this.dataList.length;
    return attachedCount + previewCount;
  }

  wouldExceedPhotoLimit(additionalCount) {
    return this.currentPhotoCount + additionalCount > this.maxPhotos;
  }

  updateDefaultPreviewVisibility() {
    const defaultPreview = this.previewTarget.querySelector(
      ".diary-inputform__default",
    );
    if (!defaultPreview) return;

    const totalPhotos = this.previewTarget.querySelectorAll(
      ".diary-inputform__foto",
    ).length;
    defaultPreview.style.display = totalPhotos >= this.maxPhotos ? "none" : "";
  }

  createPreviewContainer(id) {
    const container = document.createElement("div");
    container.setAttribute("data-file-id", id);
    container.classList.add("diary-inputform__foto");
    return container;
  }

  createPreviewImage(src, alt) {
    const img = document.createElement("img");
    img.src = src;
    img.alt = alt;
    return img;
  }

  createDeleteButton(fileId) {
    const deleteLink = document.createElement("a");
    deleteLink.classList.add("diary-inputform__foto-delete-button");
    deleteLink.setAttribute("data-action", "click->image-preview#deleteFile");
    deleteLink.dataset.fileId = fileId;
    deleteLink.href = "#";
    deleteLink.onclick = (e) => e.preventDefault();

    // Try to use SVG sprite if available
    try {
      const svgSprite = document.getElementById("svg-sprite");
      if (svgSprite && svgSprite.querySelector("#icon-delete")) {
        deleteLink.innerHTML =
          svgSprite.querySelector("#icon-delete").outerHTML;
      } else {
        deleteLink.textContent = "×";
      }
    } catch (error) {
      deleteLink.textContent = "×";
    }

    return deleteLink;
  }

  showErrorMessage(message) {
    const errorContainer = this.getOrCreateErrorContainer();

    const errorMessage = document.createElement("div");
    errorMessage.classList.add("error-message", "alert__card");
    errorMessage.setAttribute("role", "alert");
    errorMessage.setAttribute("data-controller", "flash");
    errorMessage.setAttribute("data-flash-target", "message");

    // Add error icon
    const iconSvg = `<svg aria-hidden="true" class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 8v4m0 4h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
    const iconWrapper = document.createElement("span");
    iconWrapper.innerHTML = iconSvg;

    // Add message text
    const text = document.createElement("p");
    text.textContent = message;

    // Add close button
    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.classList.add("close");
    closeButton.setAttribute("data-action", "click->flash#close");
    closeButton.setAttribute("aria-label", "close");
    closeButton.innerHTML = `<svg aria-hidden="true" class="svg-inline--fa" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M6 6l12 12m0-12L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    // Assemble the message
    errorMessage.appendChild(iconWrapper.firstChild);
    errorMessage.appendChild(text);
    errorMessage.appendChild(closeButton);

    // Add to container
    errorContainer.innerHTML = "";
    errorContainer.appendChild(errorMessage);
  }

  getOrCreateErrorContainer() {
    // Find existing container or create one
    let errorContainer =
      this.element.querySelector(".js-error-container") ||
      this.previewTarget.parentNode.querySelector(".js-error-container");

    if (!errorContainer) {
      errorContainer = document.createElement("div");
      errorContainer.classList.add("js-error-container");
      this.previewTarget.after(errorContainer);
    }

    return errorContainer;
  }

  clearErrorMessages() {
    const errorContainer = this.element.querySelector(".js-error-container");
    if (errorContainer) {
      errorContainer.innerHTML = "";
    }
  }

  getFormData(form = null) {
    const formData = form ? new FormData(form) : new FormData();

    // Only add files from dataList if they're not already in the form
    // This prevents duplicate uploads
    const existingFiles = Array.from(formData.getAll("meal_entry[photos][]"));
    this.dataList.forEach((item) => {
      // Check if the file is not already in the form
      if (
        !existingFiles.some(
          (existingFile) => existingFile.name === item.file.name,
        )
      ) {
        formData.append("meal_entry[photos][]", item.file);
      }
    });

    return formData;
  }

  updatePhotoCountDisplay() {
    const photoCountElement = document.getElementById("photo-count");
    if (photoCountElement) {
      photoCountElement.textContent = this.currentPhotoCount;
    }
  }

  addSnackToInfo(event) {
    const snack = event.currentTarget.dataset.snack;
    const additionalInfoField = this.additionalInfoTarget;
    const currentValue = additionalInfoField.value.trim();

    // Add comma and space if there's existing content
    const prefix = currentValue ? ", " : "";

    // Append the new snack
    additionalInfoField.value = currentValue + prefix + snack;
  }
}
